import {useCallback, useEffect, useRef} from 'react';
import {logHelper, tLogStyled} from 'utils/Logger';
import {Material, Mesh, MeshStandardMaterial, Object3D, PerspectiveCamera} from 'three';
import {Object3DTypedArray, useSceneStore} from 'stores/useSceneStore';
import {useFSMStore} from 'stores/useFSMStore';
import {FSMStates} from 'types/FSMStates';
import {useCameraStore} from 'stores/useCameraStore';
import {useThree} from '@react-three/fiber';

export const useParseGLTF = (scene: Object3D): void => {
  const isInitialized = useRef<boolean>(false);
  const setFSMState = useFSMStore(state => state.setFSMState);
  const {setSortedObjects} = useSceneStore(state => ({
    setSortedObjects: state.setSortedObjects
  }));
  const {setCameraBrain, setVirtualCameras} = useCameraStore(state => ({
    setCameraBrain: state.setCameraBrain,
    setVirtualCameras: state.setVirtualCameras
  }));
  const camera = useThree(state => state.camera);

  //#region Plumbing

  const processCameras = useCallback((sortedObjects: Object3DTypedArray): void => {
    camera.far = 20;
    camera.near = 0.01;
    setCameraBrain(camera as PerspectiveCamera); // r3f default camera

    if (!sortedObjects || Object.keys(sortedObjects).length === 0) return;

    const virtualCameras = sortedObjects['camera_virtual'] as PerspectiveCamera[] || [];
    setCameraTargets(virtualCameras, scene);
    setVirtualCameras(virtualCameras);

  }, [camera, scene, setCameraBrain, setVirtualCameras]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setShadowProps = useCallback(() => {
    scene.traverse((obj: Object3D) => {
      // @ts-ignore
      if (obj.isMesh && !obj.userData.tags?.noShadows) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }, [scene]);

  const processMaterials = useCallback(() => {
    scene.traverse((obj: Object3D) => {
      // @ts-ignore
      if (obj.isMesh) {
        const mesh = obj as Mesh;
        const material = mesh.material as Material;

        // material.side = FrontSide;

        if (material.name === 'background') {// TODO find another solution to differentiate ground object
          material.dithering = true;
        }

        if (material.name === 'vitrage' || material.name === 'palstic clear') {
          (material as MeshStandardMaterial).roughness = 0.05;
          (material as MeshStandardMaterial).metalness = .9;
        }

        if (material.name === 'plastic translu') {
          (material as MeshStandardMaterial).roughness = 0.5;
          (material as MeshStandardMaterial).metalness = .9;
        }
      }
    });
  }, [scene]);

  //#endregion

  // Sort objects when scene changes
  useEffect(() => {
    // testing `isInitialized.current` will prevent any future gltf parsing...
    if (scene && !isInitialized.current) {
      tLogStyled('[useParseGLTF] GLTF Scene ', logHelper.subdued, scene); // DEBUG

      // setShadowProps(); // TODO uncomment to add shadows

      processMaterials();

      const _sortedObjects = sortObjectsByType(scene);
      setSortedObjects(_sortedObjects);

      if (_sortedObjects && Object.keys(_sortedObjects).length > 0) {
        tLogStyled('[useParseGLTF] Sorted Objects ', logHelper.subdued, _sortedObjects); // DEBUG
        processCameras(_sortedObjects);
      }

      setFSMState(FSMStates.loaded); // TODO: move where parsing is done
      setFSMState(FSMStates.screenSaver); // TODO: move where parsing is done

      isInitialized.current = true;
    }
  }, [processCameras, processMaterials, scene, setFSMState, setSortedObjects]);

  // Camera offset extracted to useCameraOffset.ts

};

/**
 * Sort GLTF Scene children by their type.
 * <p>The `type` is read from `.userData.tags` of the child `Object3D`</p>
 */
const sortObjectsByType = (scene: Object3D): Object3DTypedArray => {
  const result: { [key: string]: Object3D[] } = {}; // :Object3DTypedArray
  const getTypeOf = (obj: Object3D) => obj.userData.tags?.type;
  const firstItemOf = (type: string) => !result[type];
  const initializePropertyOf = (type: string) => result[type] = [];

  scene.traverse(obj => {
    const types = getTypeOf(obj);
    if (types) {
      const splitTypes = types.split('&');
      splitTypes.forEach((type: string) => {
        if (firstItemOf(type)) initializePropertyOf(type);
        result[type].push(obj);
      });
    }
  });

  return result;
};

const setCameraTargets = (cameras: Object3D[], scene: Object3D): Object3D[] => {
  // TODO PROBLEM when loading GLTF, sometimes Cameras are renamed with unique names (CameraName => CameraName_1)
  //  but Targets are not renamed! ==> USING `userData.name` instead of `obj.name`
  cameras.forEach(cam => {
    const targetName = [cam.userData.tags?.cameraTarget, cam.userData.name + '.Target'];
    scene.traverse(obj => {
      if (targetName.includes(obj.userData.name)) {
        cam.userData.tags.cameraTarget = obj; // replace target name by its object
      }
    });
  });

  return cameras;
};
